import { useState } from "react";
import { m, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { Reveal, Eyebrow, SectionTitle } from "./shared";
import { useSite } from "../../context/SiteContext";
import { imgUrl, optImg } from "../../lib/api";

export default function GallerySection() {
  const { gallery } = useSite();
  const [cat, setCat] = useState("Todas");
  const [lightbox, setLightbox] = useState(null);

  const items = gallery.items.filter((g) => cat === "Todas" || g.category === cat);
  const cats = ["Todas", ...gallery.categories];

  const navigate = (dir) => {
    if (lightbox === null) return;
    const next = (lightbox + dir + items.length) % items.length;
    setLightbox(next);
  };

  return (
    <section id="galeria" className="py-20 lg:py-28 bg-olivedark grain relative" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Eyebrow light>Galeria</Eyebrow>
          <SectionTitle light>Momentos que ficaram para sempre</SectionTitle>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-10 flex flex-wrap gap-2" data-testid="gallery-filters">
            {cats.map((c) => (
              <button
                key={c}
                data-testid={`gallery-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setCat(c)}
                className={`text-xs uppercase tracking-[0.15em] px-4 py-2 rounded-full border transition-all duration-300 ${
                  cat === c
                    ? "bg-gold text-olivedark border-gold font-semibold"
                    : "border-cream/25 text-cream/70 hover:border-gold hover:text-goldlight"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-10 columns-1 sm:columns-2 lg:columns-3 gap-5 space-y-5">
          {items.map((g, i) => (
            <Reveal key={g.id} delay={(i % 3) * 0.06}>
              <button
                data-testid={`gallery-item-${i}`}
                onClick={() => setLightbox(i)}
                className="block w-full overflow-hidden rounded-2xl group break-inside-avoid"
              >
                <img
                  src={optImg(g.url, 800)}
                  alt={g.alt || g.category}
                  className="w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
              </button>
            </Reveal>
          ))}
        </div>
        {items.length === 0 && (
          <p className="mt-10 text-cream/60 text-sm" data-testid="gallery-empty">
            Nenhuma foto nesta categoria ainda.
          </p>
        )}
      </div>

      <AnimatePresence>
        {lightbox !== null && items[lightbox] && (
          <m.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-olivedark/95 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setLightbox(null)}
            data-testid="gallery-lightbox"
          >
            <button
              data-testid="lightbox-close"
              className="absolute top-5 right-5 text-cream/80 hover:text-goldlight p-2"
              onClick={() => setLightbox(null)}
              aria-label="Fechar"
            >
              <X className="w-7 h-7" />
            </button>
            <button
              data-testid="lightbox-prev"
              className="absolute left-3 sm:left-8 text-cream/80 hover:text-goldlight p-2"
              onClick={(e) => { e.stopPropagation(); navigate(-1); }}
              aria-label="Anterior"
            >
              <ChevronLeft className="w-9 h-9" />
            </button>
            <m.img
              key={items[lightbox].id}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              src={optImg(items[lightbox].url, 1600)}
              alt={items[lightbox].alt || ""}
              className="max-h-[85vh] max-w-full rounded-xl object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              data-testid="lightbox-next"
              className="absolute right-3 sm:right-8 text-cream/80 hover:text-goldlight p-2"
              onClick={(e) => { e.stopPropagation(); navigate(1); }}
              aria-label="Próxima"
            >
              <ChevronRight className="w-9 h-9" />
            </button>
          </m.div>
        )}
      </AnimatePresence>
    </section>
  );
}
